import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Lightweight cart for the parts/accessories catalog.
 *
 * Persists to localStorage so the cart survives a page refresh. Stores the
 * minimal shape needed (slug + qty + cached display fields) — final pricing
 * is recomputed server-side at checkout against `catalog_items` in Mongo.
 */
const CartContext = createContext(null);
const KEY = "screenzz-parts-cart-v1";

function loadInitial() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [lines, setLines] = useState(loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(lines));
    } catch {
      /* localStorage full or disabled — okay to silently no-op */
    }
  }, [lines]);

  const add = useCallback((item, qty = 1) => {
    setLines((prev) => {
      const found = prev.find((l) => l.slug === item.slug);
      if (found) {
        return prev.map((l) =>
          l.slug === item.slug ? { ...l, quantity: l.quantity + qty } : l
        );
      }
      return [
        ...prev,
        {
          slug: item.slug,
          name: item.name,
          price: item.price,
          image_url: item.image_url || null,
          quantity: qty,
        },
      ];
    });
  }, []);

  const setQty = useCallback((slug, qty) => {
    setLines((prev) =>
      prev
        .map((l) => (l.slug === slug ? { ...l, quantity: Math.max(0, qty) } : l))
        .filter((l) => l.quantity > 0)
    );
  }, []);

  const remove = useCallback((slug) => {
    setLines((prev) => prev.filter((l) => l.slug !== slug));
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const itemCount = lines.reduce((s, l) => s + l.quantity, 0);
  const itemsSubtotal = lines.reduce((s, l) => s + l.quantity * Number(l.price || 0), 0);

  return (
    <CartContext.Provider value={{ lines, add, setQty, remove, clear, itemCount, itemsSubtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
